import { Alert, Box, Button, Card, Checkbox, Grid, Tooltip, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { patchFacilitiesGlConsolidation } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { useSnackbar } from '../../../../reusable-components/snackbar';


PropTypes.GlConsolidationFacility = {
  isManual: PropTypes.bool,
  gridSx: PropTypes.object,
  heightSx: PropTypes.object,
}

export default function GlConsolidationFacility({ isManual = true, gridSx, heightSx }) {
  const {
    data: { invoiceFacilitySettings },
  } = useSelector((state) => state.invoicing);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const [unsavedData, setUnsavedData] = useState({});

  const displayedFacilitySettings = useMemo(
    () => (invoiceFacilitySettings || []).map((facility) => ({
      ...facility,
      unconsolidatedGls: isManual ? facility.manualUnconsolidatedGls : facility.unconsolidatedGls,
    })),
    [invoiceFacilitySettings, isManual]
  );

  useEffect(() => {
    setUnsavedData({});
  }, [isManual]);

  const handleSave = async () => {
    const facilitiesArrayWithIds = Object.entries(unsavedData).map(([id, data]) => ({
      id: Number(id),
      ...data,
    }));
    const response = await dispatch(patchFacilitiesGlConsolidation(facilitiesArrayWithIds, isManual));
    if (response.status === 200) {
      enqueueSnackbar('facilities settings updated successfully', { variant: 'success' });
    } else {
      enqueueSnackbar('failed to update facilities settings', { variant: 'error' });
    }
    setUnsavedData({});
  };
  const handleCancel = () => {
    apiRef.current.updateRows(displayedFacilitySettings);
    setUnsavedData({});
  };

  const handleCheckAll = (event) => {
    const isChecked = event.target.checked;
    const updatedUnsavedData = { ...unsavedData };
    displayedFacilitySettings?.forEach((facility) => {
      const originalValue = !!facility.unconsolidatedGls;
      if (originalValue !== isChecked) {
        updatedUnsavedData[facility.id] = { ...facility, unconsolidatedGls: isChecked };
      } else {
        delete updatedUnsavedData[facility.id];
      }
      apiRef.current.updateRows([{ ...facility, unconsolidatedGls: isChecked }]);
    });
    setUnsavedData(updatedUnsavedData);
  };
  const handleCellChange = (id, field, isChecked) => {
    const originalValue = !!displayedFacilitySettings?.find((facility) => facility.id === id)?.unconsolidatedGls;
    setUnsavedData((prevUnsavedData) => {
      if (originalValue !== isChecked) {
        return {
          ...prevUnsavedData,
          [id]: {
            ...prevUnsavedData[id],
            [field]: isChecked,
          },
        };
      }
      const updatedUnsavedData = { ...prevUnsavedData };
      delete updatedUnsavedData[id];
      return updatedUnsavedData;
    });
    apiRef.current.updateRows([{ id, [field]: isChecked }]);
  };
  const facilityName = {
    field: 'facilityName',
    headerName: 'Facility',
    type: 'customText',
    minWidth: 100,
    flex: 2,
  };
  const company = {
    field: 'company',
    headerName: 'Group',
    type: 'customText',
    minWidth: 100,
    flex: 2,
  };
  const unconsolidatedGls = {
    field: 'unconsolidatedGls',
    headerName: 'Unconsolidated',
    minWidth: 100,
    flex: 2,
    renderHeader: () => {
      const allChecked = Array.from(apiRef.current.getRowModels().values()).every((row) => row.unconsolidatedGls);
      return (
        <>
          Unconsolidated&nbsp;
          <Tooltip title="Select all">
            <Checkbox
              checked={allChecked}
              sx={{
                mx: 0.5,
              }}
              color="secondary"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleCheckAll(event);
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </Tooltip>
        </>
      );
    },
    renderCell: ({ id, field, value }) => (
      <Checkbox
        color="secondary"
        checked={!!value}
        onChange={(event) => {
          handleCellChange(id, field, event.target.checked);
        }}
      />
    ),
  };
  const columns = [facilityName, company, unconsolidatedGls];

  return (
    <>
      {invoiceFacilitySettings?.length > 0 && (
        <Card sx={{ p: 2, ...heightSx }}>
          <Typography variant="h5" color="primary.main">
            Unconsolidated GLs
          </Typography>
          <Alert severity="info">
            <Typography color="info.main" fontSize={'0.875rem'}>
              Enabling this feature allows for invoice to be submitted by individual GL item.
            </Typography>
          </Alert>
          <Box sx={{ maxHeight: '450px' }}>
            <CustomDataGrid
              gridId={'invoice-tab-settings'}
              forceReRender={displayedFacilitySettings}
              isLoading={false}
              hideFilterSettings
              boxSX={{ width: '100%', height: '450px' }}
              data={displayedFacilitySettings}
              gridColumns={columns}
              apiRef={apiRef}
              sx={gridSx}
            />
          </Box>
          {Object.values(unsavedData).length > 0 && (
            <Grid container justifyContent={'center'} paddingTop={2}>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                sx={{ width: 150, mr: 2 }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <AwaitButton
                variant="contained"
                color="primary"
                size="medium"
                sx={{ width: 150 }}
                onClick={handleSave}
              >
                Save
              </AwaitButton>
            </Grid>
          )}
        </Card>
      )}
    </>
  );
}
